import React, { useEffect, useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Check, X, Plus, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { http } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useResourceMutations, useResourceList } from "@/hooks/useResource";
import { useOptions } from "@/hooks/useOptions";
import { FormDrawer } from "@/components/kit/FormDrawer";
import { TextField, TextareaField } from "@/components/kit/fields";
import { SearchSelect } from "@/components/kit/SearchSelect";
import { StatusBadge } from "@/components/kit/StatusBadge";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { fmtMoney, fmtDateTime } from "@/lib/format";

const ACTIVE_WALLET = (w) => ["active", "approved"].includes((w.status || "active").toLowerCase());

export function WithdrawalDrawer({ open, item, canApprove, onOpenChange, onAddWallet, admin = false }) {
  const { user } = useAuth();
  const opts = useOptions();
  const qc = useQueryClient();
  const { create } = useResourceMutations("withdrawals");
  const { data: walletsData } = useResourceList("wallets", {});
  const [form, setForm] = useState({ amount: "", wallet_id: "", buyer_id: "" });
  const [cform, setCform] = useState({ comment: "", private_comment: "" });
  const [savingNotes, setSavingNotes] = useState(false);
  const [busy, setBusy] = useState(false);
  const isReview = !!item?.id;
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const dirty = !isReview && (!!form.amount || !!form.wallet_id || !!form.buyer_id);

  // In admin Create Payout we pick the buyer; otherwise it's the current user.
  const targetBuyer = admin ? form.buyer_id : user.id;
  const { data: bal } = useQuery({
    queryKey: ["balances", admin ? form.buyer_id || "none" : "me"],
    queryFn: async () => (await http.get(`/conversions/balances${admin && form.buyer_id ? `?buyer_id=${form.buyer_id}` : ""}`)).data,
    enabled: open && !isReview && (!admin || !!form.buyer_id),
  });

  const allWallets = walletsData?.items || [];
  const wallets = allWallets.filter((w) => w.buyer_id === targetBuyer);
  const approvedWallets = wallets.filter(ACTIVE_WALLET);
  const buyerOpts = useMemo(() => (opts.users || opts.buyers || []).map((b) => ({ value: b.id, label: `${b.tenant_seq != null ? `#${b.tenant_seq} ` : ""}${b.name}`, search: `#${b.tenant_seq} ${b.tenant_seq ?? ""} ${b.name} ${b.username || ""} ${b.email || ""}` })), [opts]);

  useEffect(() => { if (open && !isReview) setForm({ amount: "", wallet_id: "", buyer_id: "" }); }, [open, isReview]);
  useEffect(() => {
    if (open && isReview) setCform({ comment: item?.comment || "", private_comment: item?.private_comment || "" });
  }, [open, isReview, item?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const pmById = (id) => (opts.payment_methods || []).find((m) => m.id === id) || {};
  const selectedWallet = useMemo(() => wallets.find((w) => w.id === form.wallet_id), [wallets, form.wallet_id]);
  const available = Number(bal?.available_balance || 0);

  const submit = async () => {
    const amt = Number(form.amount);
    if (admin && !form.buyer_id) return toast.error("Select a user");
    if (!selectedWallet || !ACTIVE_WALLET(selectedWallet)) return toast.error("Select an approved wallet");
    if (!amt || amt <= 0) return toast.error("Enter a valid amount");
    if (amt > available) return toast.error(`Amount exceeds Available Balance (${fmtMoney(available)})`);
    setBusy(true);
    try {
      await create.mutateAsync({
        amount: amt, method: selectedWallet.method || pmById(selectedWallet.payment_method_id).name || "Wallet", wallet: selectedWallet.address,
        wallet_id: selectedWallet.id, currency: selectedWallet.currency,
        user_name: (opts.users || opts.buyers)?.find((b) => b.id === targetBuyer)?.name || user.name,
        buyer_id: targetBuyer, status: "new",
        // Fresh UUID per submit: a network retry/double-click reuses it → server returns the SAME
        // request instead of creating a second payout. A new click generates a new key = new attempt.
        __headers: { "Idempotency-Key": (crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2)}`) },
      });
      qc.invalidateQueries({ queryKey: ["balances"] });
      qc.invalidateQueries({ queryKey: ["withdrawals"] });
      qc.invalidateQueries({ queryKey: ["dashboard"] });
      toast.success("Payout requested");
      onOpenChange(false);
    } catch (e) {
      toast.error("Could not request payout", { description: e?.response?.data?.detail || "Please try again." });
    } finally { setBusy(false); }
  };

  // ---- Review (staff change payout status) ----
  const changeStatus = async (status) => {
    setBusy(true);
    try {
      await http.post(`/withdrawals/${item.id}/status`, { status });
      qc.invalidateQueries({ queryKey: ["withdrawals"] });
      qc.invalidateQueries({ queryKey: ["balances"] });
      qc.invalidateQueries({ queryKey: ["dashboard"] });
      toast.success(`Payout ${status}`);
      onOpenChange(false);
    } catch (e) {
      toast.error("Update failed", { description: e?.response?.data?.detail || "Try again." });
    } finally { setBusy(false); }
  };

  const isStaffUser = user.role !== "buyer";
  const saveNotes = async () => {
    setSavingNotes(true);
    try {
      await http.post(`/withdrawals/${item.id}/comments`, { comment: cform.comment, private_comment: cform.private_comment });
      qc.invalidateQueries({ queryKey: ["withdrawals"] });
      toast.success("Comments saved");
    } catch (e) {
      toast.error("Could not save comments", { description: e?.response?.data?.detail || "Try again." });
    } finally { setSavingNotes(false); }
  };

  if (isReview) {
    const st = item.status || "pending";
    return (
      <FormDrawer open={open} onOpenChange={onOpenChange} title="Payout request" subtitle={item.user_name} size="md"
        testId="withdrawal-review-drawer"
        footer={canApprove && st !== "rejected" ? (
          <div className="flex w-full flex-wrap items-center justify-end gap-2">
            <Button variant="ghost" disabled={busy} onClick={() => changeStatus("rejected")} data-testid="reject-withdrawal" className="gap-1.5 text-[#EF4444] hover:text-[#EF4444]"><X className="h-4 w-4" /> Reject</Button>
            {(st === "new" || st === "pending") && <Button variant="ghost" disabled={busy} onClick={() => changeStatus("in_progress")} data-testid="take-in-work" className="gap-1.5 text-[#EAB308] hover:text-[#EAB308]"><Loader2 className="h-4 w-4" /> Take in Work</Button>}
            {st !== "approved" && <Button disabled={busy} onClick={() => changeStatus("approved")} data-testid="approve-withdrawal" className="gap-1.5 bg-[#22C55E] text-black hover:bg-[#22C55E]/90"><Check className="h-4 w-4" /> Approve</Button>}
          </div>
        ) : <Button variant="ghost" onClick={() => onOpenChange(false)}>Close</Button>}>
        <div className="space-y-4">
          {[["Order ID", item.order_no || item.id?.slice(0, 8)], ["User", item.user_label || item.user_name], ["Amount", fmtMoney(item.amount)], ["Method", item.method], ["Wallet", item.wallet], ["Requested", fmtDateTime(item.created_at)]].map(([k, v]) => (
            <div key={k} className="flex items-center justify-between border-b border-white/[0.06] pb-3 text-sm">
              <span className="text-muted-foreground">{k}</span><span className="font-mono text-foreground break-all text-right">{v}</span>
            </div>
          ))}
          <div className="flex items-center justify-between text-sm"><span className="text-muted-foreground">Status</span><StatusBadge status={st} /></div>
          {isStaffUser ? (
            <div className="space-y-3 rounded-lg border border-white/[0.08] bg-[#0A0A0A] p-3" data-testid="withdrawal-notes">
              <TextareaField label="Comment" hint="Visible to the payout requester and all staff."
                value={cform.comment} onChange={(v) => setCform((f) => ({ ...f, comment: v }))} testId="withdrawal-comment" rows={2} />
              <TextareaField label="Private Comment" hint="Internal — visible to staff only, never shown to the user."
                value={cform.private_comment} onChange={(v) => setCform((f) => ({ ...f, private_comment: v }))} testId="withdrawal-private-comment" rows={2} />
              <Button size="sm" disabled={savingNotes} onClick={saveNotes} data-testid="save-payout-comments"
                className="h-8 gap-1.5 bg-white text-xs text-black hover:bg-white/90">
                {savingNotes ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />} Save comments
              </Button>
            </div>
          ) : (
            !!item.comment && (
              <div className="rounded-lg border border-white/[0.08] bg-[#0A0A0A] p-3" data-testid="withdrawal-comment-view">
                <p className="text-xs text-muted-foreground">Comment</p>
                <p className="mt-1 whitespace-pre-wrap text-sm text-foreground">{item.comment}</p>
              </div>
            )
          )}
        </div>
      </FormDrawer>
    );
  }

  return (
    <FormDrawer open={open} onOpenChange={onOpenChange} title={admin ? "Create Payout" : "Request Withdrawal"}
      subtitle={admin ? "Create a payout order on behalf of a user" : "Withdraw from your Available Balance"} size="md" dirty={dirty}
      onSave={submit} saving={create.isPending} saveLabel={admin ? "Create payout" : "Submit request"} testId="withdrawal-drawer">
      <div className="space-y-5">
        {admin && (
          <div>
            <label className="mb-1.5 block text-sm text-foreground">User</label>
            <SearchSelect value={form.buyer_id} onChange={(v) => set("buyer_id", v)} options={buyerOpts}
              placeholder="Select a user" searchPlaceholder="Search users..." widthClass="w-[360px]" testId="payout-user" />
          </div>
        )}

        {(!admin || form.buyer_id) && (
          <div className="grid grid-cols-2 gap-3">
            <div className="rounded-lg border border-white/[0.08] bg-[#0A0A0A] p-3" data-testid="wd-hold-hint">
              <p className="text-xs text-muted-foreground">Hold Balance</p>
              <p className="mt-0.5 text-lg font-semibold text-[#EAB308]">{fmtMoney(bal?.hold_balance)}</p>
            </div>
            <div className="rounded-lg border border-white/[0.08] bg-[#0A0A0A] p-3" data-testid="wd-available-hint">
              <p className="text-xs text-muted-foreground">Available Balance</p>
              <p className="mt-0.5 text-lg font-semibold text-[#22C55E]">{fmtMoney(available)}</p>
            </div>
          </div>
        )}

        {(!admin || form.buyer_id) && (
          <div data-testid="wd-wallets">
            <div className="mb-1.5 flex items-center justify-between">
              <label className="block text-sm text-foreground">Pay to wallet</label>
              {!admin && onAddWallet && (
                <Button size="sm" variant="outline" onClick={() => onAddWallet()} data-testid="wd-add-wallet" className="h-7 gap-1.5 border-white/15 text-xs"><Plus className="h-3.5 w-3.5" /> Add wallet</Button>
              )}
            </div>

            {wallets.length === 0 ? (
              <div className="rounded-lg border border-dashed border-white/[0.15] p-4 text-center text-sm text-muted-foreground" data-testid="wd-no-wallets">
                No wallet{admin ? " for this user" : ""} yet.{admin ? "" : " Add one to request a withdrawal."}
              </div>
            ) : (
              <div className="space-y-2">
                {wallets.map((w) => {
                  const st = (w.status || "active").toLowerCase();
                  const selectable = ACTIVE_WALLET(w);
                  const selected = form.wallet_id === w.id;
                  const method = w.method || pmById(w.payment_method_id).name || "Wallet";
                  const addr = w.address || "";
                  return (
                    <button type="button" key={w.id} disabled={!selectable}
                      onClick={() => selectable && set("wallet_id", w.id)}
                      data-testid={`wd-wallet-${w.id}`}
                      className={cn(
                        "w-full rounded-lg border p-3 text-left transition-colors",
                        selected ? "border-[#22C55E]/60 bg-[#22C55E]/10" : "border-white/[0.08] bg-[#0A0A0A]",
                        selectable ? "cursor-pointer hover:border-white/20" : "cursor-not-allowed opacity-60")}>
                      <div className="flex items-center justify-between gap-2">
                        <span className="truncate text-sm font-medium text-foreground">{w.label || method}</span>
                        <StatusBadge status={st === "active" ? "approved" : st} />
                      </div>
                      <div className="mt-0.5 truncate text-xs text-muted-foreground">
                        {method}{w.network ? ` · ${w.network}` : ""} · <span className="font-mono">{addr.slice(0, 18)}{addr.length > 18 ? "…" : ""}</span>
                      </div>
                      {st === "pending" && <div className="mt-1 text-xs text-[#EAB308]">Awaiting approval</div>}
                      {st === "rejected" && <div className="mt-1 text-xs text-[#EF4444]">Rejected{w.rejected_reason ? `: ${w.rejected_reason}` : ""}</div>}
                      {st === "disabled" && <div className="mt-1 text-xs text-muted-foreground">Disabled — cannot be used</div>}
                    </button>
                  );
                })}
              </div>
            )}

            {wallets.length > 0 && approvedWallets.length === 0 && (
              <div className="mt-2 rounded-lg border border-[#EAB308]/30 bg-[#EAB308]/5 p-3 text-xs text-[#EAB308]" data-testid="wd-awaiting-approval">
                Your wallet was added and is awaiting approval. You can request a withdrawal once it is approved.
              </div>
            )}
          </div>
        )}

        {selectedWallet && (
          <div className="rounded-lg border border-white/[0.08] bg-[#0A0A0A] p-3 text-xs text-muted-foreground" data-testid="withdrawal-wallet-meta">
            <div className="flex justify-between"><span>Method</span><span className="text-foreground">{selectedWallet.method}{selectedWallet.network ? ` · ${selectedWallet.network}` : ""}</span></div>
            <div className="mt-1 flex justify-between"><span>Address</span><span className="font-mono text-foreground break-all text-right">{selectedWallet.address}</span></div>
          </div>
        )}

        {(!admin || form.buyer_id) && <TextField label="Amount" type="number" value={form.amount} onChange={(v) => set("amount", v)} testId="withdrawal-amount" />}
      </div>
    </FormDrawer>
  );
}
